import { ChatBubbleOutline, Close, Delete, Send } from '@mui/icons-material';
import {
    CircularProgress,
    IconButton,
    TextField,
    Typography,
    Box,
    Button,
} from '@mui/material';
import axios, { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import constants from '../config/constants';
import strings from '../config/strings';
import { useLanguage } from '../context/language.context';

interface Message {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: Date;
}

interface ChatDialogProps {
    open: boolean;
    onClose: () => void;
    userType?: 'expert' | 'therapist';
    instructorId?: string;
}

const ChatDialog = ({ open, onClose, userType = 'expert', instructorId }: ChatDialogProps) => {
    const [question, setQuestion] = useState<string>('');
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { t, dir } = useLanguage();

    useEffect(() => {
        // Scroll to bottom when new message is added
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isLoading]);

    const cleanHtmlResponse = (text: string): string => {
        if (!text) return text;

        // Remove ```html or ``` at the beginning
        let cleaned = text.trim();
        if (cleaned.startsWith('```html')) {
            cleaned = cleaned.substring(7).trim();
        } else if (cleaned.startsWith('```')) {
            cleaned = cleaned.substring(3).trim();
        }

        // Remove ``` at the end
        if (cleaned.endsWith('```')) {
            cleaned = cleaned.substring(0, cleaned.length - 3).trim();
        }

        return cleaned;
    };

    const sendMessage = async () => {
        if (!question.trim() || isLoading) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            text: question.trim(),
            isUser: true,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        const currentQuestion = question.trim();
        setQuestion('');
        setIsLoading(true);

        try {
            const token = localStorage.getItem(strings.$GH_TOKEN);
            if (!token) {
                throw new Error('לא נמצא טוקן');
            }

            const requestData: any = {
                question: currentQuestion,
                type: userType,
            };

            // Add instructor_id if exists
            if (instructorId) {
                requestData.instructor_id = instructorId;
            }

            // Add conversation_id if exists (for follow-up questions)
            if (conversationId) {
                requestData.conversation_id = conversationId;
            }

            const response = await axios.post(
                `${constants.API_URL}/instructor/experts-questions`,
                requestData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            // Save conversation_id for next messages
            if (response?.data?.conversation_id) {
                setConversationId(response.data.conversation_id);
            }

            // Clean HTML response from markdown code blocks
            const rawAnswer = response?.data?.answer || response?.data?.message || t('chatNoAnswer');
            const cleanedAnswer = cleanHtmlResponse(rawAnswer);

            const aiMessage: Message = {
                id: (Date.now() + 1).toString(),
                text: cleanedAnswer,
                isUser: false,
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, aiMessage]);
        } catch (error: any) {
            console.error('Error sending message:', error);
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                text: error?.response?.data?.message || error?.message || t('chatError'),
                isUser: false,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        setMessages([]);
        setConversationId(null);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && question.trim() && !isLoading) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (!open) return null;

    return (
        <>
            {/* Backdrop */}
            <Box
                onClick={onClose}
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 1299,
                }}
            />
            {/* Chat Panel */}
            <Box
                sx={{
                    position: 'fixed',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '400px',
                    maxWidth: '90vw',
                    zIndex: 1300,
                    backgroundColor: 'white',
                    boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    dir: dir,
                }}
            >
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'white',
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('chatTitle')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {messages.length > 0 && (
                        <IconButton onClick={clearChat} size="small" color="error">
                            <Delete />
                        </IconButton>
                    )}
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </Box>

                {/* Messages Container */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        backgroundColor: '#f9f9f9',
                    }}
                >
                    {messages.length === 0 ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                opacity: 0.5,
                            }}
                        >
                            <ChatBubbleOutline sx={{ fontSize: 64, mb: 2 }} color="primary" />
                            <Typography
                                variant="body1"
                                textAlign="center"
                                sx={{ maxWidth: '80%', color: 'text.secondary' }}
                            >
                            {t('chatWelcome')}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {messages.map((message) => (
                                <Box
                                    key={message.id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            maxWidth: '80%',
                                            p: 1.5,
                                            borderRadius: 2,
                                            backgroundColor: message.isUser ? 'primary.main' : '#e0e0e0',
                                            color: message.isUser ? 'white' : 'text.primary',
                                            borderBottomRightRadius: message.isUser ? 0.5 : 2,
                                            borderBottomLeftRadius: message.isUser ? 2 : 0.5,
                                        }}
                                    >
                                        {message.isUser ? (
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {message.text}
                                            </Typography>
                                        ) : (
                                            <Typography
                                                variant="body1"
                                                component="div"
                                                sx={{
                                                    '& p': {
                                                        margin: 0,
                                                        marginBottom: 1,
                                                        textAlign: 'right',
                                                        direction: 'rtl',
                                                    },
                                                    '& a': {
                                                        color: 'inherit',
                                                        textDecoration: 'underline',
                                                    },
                                                    '& h1, & h2, & h3': {
                                                        marginTop: 1,
                                                        marginBottom: 1,
                                                        textAlign: 'right',
                                                    },
                                                    '& ul, & ol': {
                                                        textAlign: 'right',
                                                        direction: 'rtl',
                                                        paddingRight: 2,
                                                    },
                                                    '& li': {
                                                        textAlign: 'right',
                                                        direction: 'rtl',
                                                    },
                                                    wordBreak: 'break-word',
                                                }}
                                                dangerouslySetInnerHTML={{ __html: message.text }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </>
                    )}

                    {isLoading && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                            }}
                        >
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: '#e0e0e0',
                                    borderBottomLeftRadius: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <CircularProgress size={16} />
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    {t('chatThinking')}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <div ref={messagesEndRef} />
                </Box>

                {/* Input Container */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: '1px solid #eee',
                        backgroundColor: 'white',
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-end',
                    }}
                >
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder={t('chatPlaceholder')}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                        variant="outlined"
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={sendMessage}
                        disabled={!question.trim() || isLoading}
                        sx={{
                            minWidth: 44,
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            p: 0,
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <Send />
                        )}
                    </Button>
                </Box>
            </Box>
        </>
    );
};

export default ChatDialog;
